import type { StepPlanInfoId } from '@az3fr/core';
import { PlusIcon } from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import {
	SetRowV2,
	SetsTable,
	SetsTableHeaderV2,
} from '@/features/workout-creator/ui/v2/sets-editor/components';
import {
	useSetActions,
	useSetsEditorData,
} from '@/features/workout-creator/ui/v2/sets-editor/hooks';

function SetsEditorV2({ stepPlanId }: { stepPlanId: StepPlanInfoId }) {
	const data = useSetsEditorData(stepPlanId);
	const { addSet } = useSetActions();
	const metricLength = data?.populatedTrackedMetrics.length ?? null;

	// Memoize grid template to prevent style recalculation
	const gridTemplateColumns = useMemo(() => {
		if (!metricLength) {
			return '';
		}
		return `60px repeat(${metricLength}, minmax(80px, 1fr))`;
		// @todo: maybe make this a bit tighter?
	}, [metricLength]);

	// Early return if data is not available
	if (!data) {
		return null;
	}

	const { stepPlan, sectionPlan, setPlans, populatedTrackedMetrics } = data;

	// Determine if we should show the "Add Set" button
	const showAddSetButton =
		stepPlan.repetitionMode === 'explicit' && sectionPlan.sectionType === 'standard';

	const handleAddSet = () => {
		addSet(stepPlanId);
	};

	return (
		<div className="overflow-hidden rounded-lg border border-gray-200">
			<SetsTable gridTemplateColumns={gridTemplateColumns}>
				{/* --- Table Header --- */}
				<SetsTableHeaderV2
					metrics={populatedTrackedMetrics}
					repetitionMode={stepPlan.repetitionMode}
					gridTemplateColumns={gridTemplateColumns}
				/>

				{/* --- Table Rows --- */}
				{setPlans.map((setPlan, index) => (
					<div
						key={setPlan.id}
						className="grid border-gray-200 border-b last:border-b-0"
						style={{ gridTemplateColumns }}
					>
						<SetRowV2
							orderIndex={index}
							setPlan={setPlan}
							metrics={populatedTrackedMetrics}
							stepPlan={stepPlan}
							sectionPlan={sectionPlan}
						/>
					</div>
				))}

				{/* --- Add Set Button --- */}
				{showAddSetButton && (
					<div className="flex size-full items-center justify-center py-1">
						<button
							type="button"
							onClick={handleAddSet}
							className="flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium text-gray-600 text-xs transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none active:text-gray-900 sm:gap-2 sm:px-3 sm:py-2 sm:text-xs"
						>
							<PlusIcon className="size-3.5 sm:size-4" />
							Add Set
						</button>
					</div>
				)}
			</SetsTable>
		</div>
	);
}

// Memoize the main component to prevent unnecessary re-renders
const MemoizedSetsEditorV2 = memo(SetsEditorV2);

export { MemoizedSetsEditorV2 as SetsEditorV2 };
